import React from "react";
import { Button, Form, Input, Modal, notification, Segmented, Select, Spin, Tag } from "antd";
import { SearchModalFooter, Gif } from "./components";
import { SearchOutlined } from "@ant-design/icons";
import { modalSelector } from "../../../../../store/selector/modalSelector";
import { useDispatch, useSelector } from "react-redux";
import { searchClosed } from "../../../../../store/modalSlice/modalSlice";
import { file, apiUrl } from "../../../../../../../static/api";
import { searchFormFields } from "../../../../../../../static/searchForm";
import { addImage } from "../../../../../../../store/slices/foldersSlice";
import axiosInstance from "../../../../../../../helpers/axiosConfig";
import { FoldersSelector } from "../../../../../../../store/selectors";
import { track, getFormat } from "../../../../../../../helpers/analytics";

import "./style.css";

const SOURCE_CONFIG = {
  giphy: {
    label: 'Giphy',
    searchEndpoint: 'giphy-search',
    importEndpoint: 'giphy',
    importKey: 'ids',
    idField: 'id',
    title: 'Search Giphy',
    hasRatings: true,
  },
  klipy: {
    label: 'KLIPY',
    searchEndpoint: 'klipy-search',
    importEndpoint: 'klipy',
    importKey: 'slugs',
    idField: 'id',
    title: 'Search KLIPY',
    hasRatings: false,
  },
  redgifs: {
    label: 'RedGIFs',
    searchEndpoint: 'redgifs-search',
    importEndpoint: 'redgifs',
    importKey: 'ids',
    idField: 'id',
    title: 'Search RedGIFs',
    hasRatings: false,
  },
};

export const ModalSearch = () => {
  const dispatch = useDispatch();

  const { folderItem } = useSelector(FoldersSelector);
  const { search } = useSelector(modalSelector);

  const [fields, setFields] = React.useState(searchFormFields);

  const [gifs, setGifs] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [hasNextPage, setHasNextPage] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [addGifs, setAddGifs] = React.useState([]);
  const [isAdding, setIsAdding] = React.useState(false);
  const [selectedRatings, setSelectedRatings] = React.useState(['g', 'pg', 'pg-13', 'r']);
  const [selectedOrientation, setSelectedOrientation] = React.useState('all');
  const [selectedOrder, setSelectedOrder] = React.useState('top');
  const [activeSource, setActiveSource] = React.useState('giphy');
  const [searchMode, setSearchMode] = React.useState('search'); // 'search' | 'ai'
  const [aiTerms, setAiTerms] = React.useState([]);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiPrompt, setAiPrompt] = React.useState('');

  const resultsRef = React.useRef(null);

  // Infinite scroll handler
  React.useEffect(() => {
    const container = resultsRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isLoadingMore || !hasNextPage || searchMode === 'ai') return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 300) {
        loadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingMore, hasNextPage, searchMode, offset, currentPage, searchValue, activeSource, selectedRatings, selectedOrientation, selectedOrder]);

  // When modal opens, use initial query (if provided) or compilation name as search term
  React.useEffect(() => {
    if (search) {
      track('search_modal_opened', { source: activeSource });
      let initialQuery = '';
      let initialSource = 'giphy';

      if (typeof search === 'object' && search.query) {
        initialQuery = search.query;
        initialSource = search.source || 'giphy';
      } else if (typeof search === 'string') {
        initialQuery = search;
      } else {
        initialQuery = folderItem?.name || '';
      }

      if (initialSource !== activeSource) {
        setActiveSource(initialSource);
      }

      if (initialQuery) {
        setSearchValue(initialQuery);
        const updatedFields = searchFormFields.map(field =>
          Array.isArray(field.name) && field.name[0] === 'search'
            ? { ...field, value: initialQuery }
            : field
        );
        setFields(updatedFields);
        fetchGifs(0, initialQuery, selectedRatings, selectedOrientation, initialSource);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const applyFilters = (data, source, orientation) => {
    let filteredData = data;

    // Proxy RedGIFs thumbnails through our backend to avoid CDN hotlink issues
    if (source === 'redgifs') {
      filteredData = filteredData.map((gif) => ({
        ...gif,
        url: gif.url ? `${apiUrl}${file}/media-proxy?url=${encodeURIComponent(gif.url)}` : '',
      }));
    }

    // Client-side orientation filtering
    if (orientation && orientation !== 'all' && filteredData.length > 0) {
      filteredData = filteredData.filter((gif) => {
        if (gif.width && gif.height) {
          const aspectRatio = gif.width / gif.height;
          if (orientation === 'landscape') return aspectRatio > 1.1;
          if (orientation === 'portrait') return aspectRatio < 0.9;
          if (orientation === 'square') return aspectRatio >= 0.9 && aspectRatio <= 1.1;
        }
        return true;
      });
    }

    return filteredData;
  };

  const fetchGifs = (page, search, ratings = selectedRatings, orientation = selectedOrientation, source = activeSource, order = selectedOrder, append = false) => {
    const condition = search === undefined ? searchValue : search;
    const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.giphy;

    if (!append) {
      setIsLoadingMore(false);
    }

    let params;
    let endpoint;

    if (source === 'giphy') {
      params = {
        q: condition,
        offset: page,
        limit: 50,
      };
      if (ratings && ratings.length > 0 && ratings.length < 4) {
        params.ratings = ratings;
      }
      endpoint = `${file}/${config.searchEndpoint}`;
    } else if (source === 'klipy') {
      const pageNum = page === 0 ? 1 : Math.floor(page / 50) + 1;
      params = {
        q: condition,
        page: pageNum,
        limit: 50,
      };
      endpoint = `${file}/${config.searchEndpoint}`;
    } else if (source === 'redgifs') {
      const pageNum = page === 0 ? 1 : Math.floor(page / 40) + 1;
      params = {
        q: condition,
        page: pageNum,
        count: 40,
        order: order,
      };
      endpoint = `${file}/${config.searchEndpoint}`;
    }

    axiosInstance
      .get(endpoint, { params })
      .then((res) => {
        let filteredData = applyFilters(res.data.data || [], source, orientation);

        if (filteredData.length === 0 && !append) {
          notification.info({
            message: `No results found, try a different search`,
          });
          setGifs([]);
          setHasNextPage(false);
        } else {
          // Normalize pagination across providers
          if (source === 'giphy') {
            const newOffset = res.data.pagination?.offset || 0;
            setOffset(newOffset);
            setCurrentPage(Math.floor(newOffset / 50) + 1);
            setHasNextPage(filteredData.length >= 50);
          } else if (source === 'klipy') {
            const klipyPage = res.data.pagination?.offset ? Math.floor(res.data.pagination.offset / 50) + 1 : (params.page || 1);
            setCurrentPage(klipyPage);
            setOffset((klipyPage - 1) * 50);
            setHasNextPage(res.data.pagination?.hasNext || false);
          } else if (source === 'redgifs') {
            const rgPage = res.data.pagination?.page || 1;
            setCurrentPage(rgPage);
            setOffset((rgPage - 1) * 40);
            setHasNextPage(rgPage < (res.data.pagination?.pages || 1));
          }

          if (append) {
            // Deduplicate when appending
            setGifs((prev) => {
              const existingIds = new Set(prev.map(g => g.id));
              const newGifs = filteredData.filter(g => !existingIds.has(g.id));
              return [...prev, ...newGifs];
            });
          } else {
            setGifs(filteredData);
          }

          if (!append) {
            track('search_regular', { source, query: condition, resultCount: filteredData.length });
          }
        }
      })
      .catch((e) => {
        notification.open({
          message: e.response?.data?.message || e.message,
        });
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  };

  const loadMore = () => {
    if (isLoadingMore || !hasNextPage || !searchValue) return;
    setIsLoadingMore(true);

    const pageSize = activeSource === 'redgifs' ? 40 : 50;
    const nextOffset = offset + pageSize;
    fetchGifs(nextOffset, searchValue, selectedRatings, selectedOrientation, activeSource, selectedOrder, true);
  };

  const fetchAiGifs = (prompt) => {
    setAiLoading(true);
    setAiTerms([]);
    setAiPrompt(prompt);
    setGifs([]);
    setHasNextPage(false);

    axiosInstance
      .get(`${file}/ai-search`, {
        params: {
          prompt,
          source: activeSource,
          count: 40,
        },
        timeout: 30000,
      })
      .then((res) => {
        let filteredData = applyFilters(res.data.data || [], activeSource, selectedOrientation);

        setAiTerms(res.data.aiTerms || []);
        track('search_ai', { source: activeSource, prompt, resultCount: filteredData.length, termsGenerated: res.data.aiTerms });
        if (filteredData.length === 0) {
          notification.info({ message: 'No results found, try a different prompt' });
        }
        setGifs(filteredData);
      })
      .catch((e) => {
        notification.error({
          message: 'AI search failed',
          description: e.response?.data?.message || e.message,
        });
      })
      .finally(() => {
        setAiLoading(false);
      });
  };

  const onFinish = async (values) => {
    const { search } = values;
    setSearchValue(search);
    setHasNextPage(true);
    if (searchMode === 'ai') {
      fetchAiGifs(search);
    } else {
      await fetchGifs(0, search, selectedRatings, selectedOrientation, activeSource);
    }
  };

  const handleSourceChange = (value) => {
    setActiveSource(value);
    setGifs([]);
    setAddGifs([]);
    setOffset(0);
    setCurrentPage(1);
    setHasNextPage(true);
    setSelectedOrder('top');
    if (searchValue) {
      fetchGifs(0, searchValue, selectedRatings, selectedOrientation, value);
    }
  };

  const handleRatingChange = (value) => {
    const ratings = Array.isArray(value) ? value : value ? [value] : ['g', 'pg', 'pg-13', 'r'];
    setSelectedRatings(ratings);
    if (searchValue) {
      fetchGifs(0, searchValue, ratings, selectedOrientation);
    }
  };

  const handleOrientationChange = (value) => {
    setSelectedOrientation(value);
    if (searchValue) {
      fetchGifs(0, searchValue, selectedRatings, value);
    }
  };

  const handleOrderChange = (value) => {
    setSelectedOrder(value);
    if (searchValue) {
      fetchGifs(0, searchValue, selectedRatings, selectedOrientation, activeSource, value);
    }
  };

  const handlePillClick = (term) => {
    track('ai_pill_clicked', { source: activeSource, term, originalPrompt: aiPrompt });
    setSearchMode('search');
    setSearchValue(term);
    setHasNextPage(true);
    const updatedFields = searchFormFields.map(field =>
      Array.isArray(field.name) && field.name[0] === 'search'
        ? { ...field, value: term }
        : field
    );
    setFields(updatedFields);
    fetchGifs(0, term, selectedRatings, selectedOrientation, activeSource);
  };

  const handleOk = async () => {
    if (addGifs.length === 0) {
      notification.warning({
        message: "Please select at least one GIF to add",
      });
      return;
    }

    if (!folderItem || !folderItem.id) {
      notification.error({
        message: "Please select a playlist first",
      });
      return;
    }

    if (isAdding) return;
    setIsAdding(true);

    const config = SOURCE_CONFIG[activeSource] || SOURCE_CONFIG.giphy;

    try {
      const idsToSend = addGifs.map((id) => String(id));

      notification.info({
        message: `Adding ${addGifs.length} GIF(s)...`,
        description: 'This may take a moment, especially for multiple large files.',
        duration: 3,
      });

      const body = { [config.importKey]: idsToSend };

      const res = await axiosInstance.post(
        `${file}/${folderItem.id}/${config.importEndpoint}`,
        body,
        { timeout: 120000 },
      );

      const arr = res.data.map((item) => ({
        ...item,
        timePerSlide: folderItem.timePerSlide || 4000,
        transitionType: folderItem.transitionType || 'fadeInOut',
      }));

      dispatch(addImage(arr));
      track('gifs_added', {
        source: activeSource,
        searchMode,
        count: arr.length,
        formats: arr.map(item => getFormat(item.width, item.height)),
      });
      setAddGifs([]);
      setGifs([]);
      dispatch(searchClosed());
      notification.success({
        message: `Successfully added ${arr.length} GIF(s) to playlist`,
      });
    } catch (error) {
      let errorMessage = "Failed to add GIFs to playlist";
      let errorDescription = "";

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = "Request timed out";
        errorDescription = "The request took too long. Please try adding fewer GIFs at once or try again.";
      } else if (error.response?.status === 400) {
        const responseData = error.response.data;
        if (Array.isArray(responseData.message)) {
          errorMessage = "Validation error";
          errorDescription = responseData.message.join(', ');
        } else if (responseData.message) {
          errorMessage = responseData.message;
          errorDescription = responseData.error || error.response.statusText;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        errorDescription = error.response.data.error || error.response.statusText;
      } else if (error.message) {
        errorMessage = error.message;
      }

      notification.error({
        message: errorMessage,
        description: errorDescription,
        duration: 5,
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleCancel = async () => {
    setFields(searchFormFields);
    setAddGifs([]);
    setGifs([]);
    dispatch(searchClosed());
  };

  const handleChecked = (e, checked, id) => {
    if (checked) {
      setAddGifs((prevState) => {
        if (prevState.includes(id)) return prevState;
        return prevState.concat(id);
      });
    } else {
      const newArr = addGifs.filter((item) => item !== id);
      setAddGifs(newArr);
    }
  };

  React.useEffect(() => {
    if (addGifs.length > 10) {
      notification.warning({
        message: `You can't add more than 10 gifs at one time`,
      });
    }
  }, [addGifs.length]);

  const renderGif = () =>
    gifs?.map((gif) => (
      <div key={gif.id} className="masonry-item">
        <Gif
          item={gif}
          onChecked={handleChecked}
          condition={addGifs.length}
          selectedIds={addGifs}
        />
      </div>
    ));

  const config = SOURCE_CONFIG[activeSource] || SOURCE_CONFIG.giphy;

  return (
    <>
      <Modal
        title={config.title}
        open={search}
        closable={true}
        onCancel={handleCancel}
        centered={false}
        keyboard={true}
        className={"search__modal"}
        footer={
          <SearchModalFooter
            onCancel={handleCancel}
            onOk={handleOk}
            count={addGifs.length}
            loading={isAdding}
            source={activeSource}
            totalLoaded={gifs.length}
          />
        }
      >
        <div className="search__source-selector">
          <Segmented
            value={activeSource}
            onChange={handleSourceChange}
            options={[
              { label: 'Giphy', value: 'giphy' },
              { label: 'KLIPY', value: 'klipy' },
              { label: 'RedGIFs', value: 'redgifs' },
            ]}
            block
          />
        </div>

        <Form
          className={"search__modal__form"}
          name="basic"
          layout={"vertical"}
          fields={fields}
          onFinish={onFinish}
          onFieldsChange={(_, allFields) => {
            setFields(allFields);
          }}
          autoComplete="off"
        >
          <div className="form__search__section">
            <Segmented
              value={searchMode}
              onChange={(val) => setSearchMode(val)}
              options={[
                { label: 'Search', value: 'search' },
                { label: 'AI Search', value: 'ai' },
              ]}
              className="search-mode-toggle"
            />
            <Form.Item name="search" className="section__search_input">
              <Input
                autoComplete=""
                placeholder={searchMode === 'ai'
                  ? 'Describe what you\'re looking for...'
                  : `Search ${config.label}...`}
                prefix={<SearchOutlined />}
              />
            </Form.Item>

            <div className="form__filters__section">
              {config.hasRatings && (
                <Form.Item label="Rating" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    value={selectedRatings}
                    onChange={handleRatingChange}
                    placeholder="Ratings"
                    style={{ minWidth: 140 }}
                    maxTagCount={1}
                    options={[
                      { label: 'G', value: 'g' },
                      { label: 'PG', value: 'pg' },
                      { label: 'PG-13', value: 'pg-13' },
                      { label: 'R', value: 'r' },
                    ]}
                  />
                </Form.Item>
              )}

              <Form.Item label="Orientation" style={{ marginBottom: 0 }}>
                <Select
                  value={selectedOrientation}
                  onChange={handleOrientationChange}
                  style={{ minWidth: 120 }}
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Landscape', value: 'landscape' },
                    { label: 'Portrait', value: 'portrait' },
                    { label: 'Square', value: 'square' },
                  ]}
                />
              </Form.Item>

              {activeSource === 'redgifs' && (
                <Form.Item label="Order" style={{ marginBottom: 0 }}>
                  <Select
                    value={selectedOrder}
                    onChange={handleOrderChange}
                    style={{ minWidth: 120 }}
                    options={[
                      { label: 'Top', value: 'top' },
                      { label: 'Score', value: 'score' },
                      { label: 'Trending', value: 'trending' },
                      { label: 'Latest', value: 'latest' },
                      { label: 'Top (Week)', value: 'top7' },
                      { label: 'Top (Month)', value: 'top28' },
                    ]}
                  />
                </Form.Item>
              )}
            </div>

            <Button type="text" htmlType="submit" disabled={aiLoading}>
              {searchMode === 'ai' ? 'Generate' : 'Search'}
            </Button>
          </div>
        </Form>

        {aiTerms.length > 0 && (
          <div className="ai-terms-pills">
            <span className="ai-terms-label">AI search terms:</span>
            {aiTerms.map((term) => (
              <Tag
                key={term}
                className="ai-term-pill"
                onClick={() => handlePillClick(term)}
              >
                {term}
              </Tag>
            ))}
          </div>
        )}

        {aiLoading && (
          <div className="ai-loading">
            <Spin size="small" />
            <span>Generating search queries...</span>
          </div>
        )}

        <div className="giphy__results__scroll" ref={resultsRef}>
          <div className="giphy__results__masonry">
            {renderGif()}
          </div>
          {isLoadingMore && (
            <div className="load-more-spinner">
              <Spin size="small" />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
